import React, { useEffect, useRef, useState } from "react";

interface AudioWaveProps {
    isActive: boolean;
    color?: string;
    height?: number;
    width?: number;
    barCount?: number;
    // New props for real-time audio visualization
    audioStream?: MediaStream;
    analyzerNode?: AnalyserNode;
    visualizationStyle?: "waveform" | "bars" | "rounded" | "circular";
    // Optional callback for volume level (0-1)
    onVolumeChange?: (level: number) => void;
}

const AudioWave: React.FC<AudioWaveProps> = ({
    isActive,
    color = "#3B82F6", // Default to a nice blue
    height = 40,
    width = 100,
    barCount = 16,
    audioStream,
    analyzerNode,
    visualizationStyle = "bars",
    onVolumeChange,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

    const [usingRealAudio, setUsingRealAudio] = useState<boolean>(false);

    // Set up audio analysis
    useEffect(() => {
        // Don't set up audio analysis if not active
        if (!isActive) return;

        // Clean up function
        const cleanup = () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }

            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
                sourceNodeRef.current = null;
            }

            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close().catch(console.error);
                audioContextRef.current = null;
            }

            analyserRef.current = null;
            setUsingRealAudio(false);
        };

        // If given an analyzer node directly, use it
        if (analyzerNode) {
            analyserRef.current = analyzerNode;
            setUsingRealAudio(true);
            return cleanup;
        }

        // If given an audio stream, create our own analyzer
        if (audioStream && audioStream.active) {
            try {
                // Create audio context
                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                audioContextRef.current = new AudioContext();

                // Create analyzer
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 256;
                analyserRef.current.smoothingTimeConstant = 0.7;

                // Create source from stream
                sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(audioStream);
                sourceNodeRef.current.connect(analyserRef.current);

                setUsingRealAudio(true);
            } catch (err) {
                console.error("Error setting up audio analysis:", err);
                cleanup();
            }
        } else {
            setUsingRealAudio(false);
        }

        return cleanup;
    }, [audioStream, analyzerNode, isActive]);

    // Visualization animation loop
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Clean up any existing animation frame
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // If not active, draw inactive state
        if (!isActive) {
            drawInactiveState(ctx, canvas.width, canvas.height, barCount, color);
            return;
        }

        // If using real audio, visualize it
        if (usingRealAudio && analyserRef.current) {
            const visualize = () => {
                if (!ctx || !analyserRef.current) return;

                // Clear canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Choose visualization based on style
                switch (visualizationStyle) {
                    case "waveform":
                        drawWaveform(ctx, canvas.width, canvas.height, color, analyserRef.current);
                        break;
                    case "circular":
                        drawCircular(ctx, canvas.width, canvas.height, color, analyserRef.current);
                        break;
                    case "rounded":
                        drawRoundedBars(ctx, canvas.width, canvas.height, color, analyserRef.current, barCount);
                        break;
                    case "bars":
                    default:
                        drawBars(ctx, canvas.width, canvas.height, color, analyserRef.current, barCount);
                        break;
                }

                // Calculate and report volume level if callback provided
                if (onVolumeChange) {
                    const volumeLevel = calculateVolumeLevel(analyserRef.current);
                    onVolumeChange(volumeLevel);
                }

                animationRef.current = requestAnimationFrame(visualize);
            };

            visualize();
        } else {
            // Fall back to animated visualization
            const animate = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                drawAnimatedBars(ctx, canvas.width, canvas.height, barCount, color);
                animationRef.current = requestAnimationFrame(animate);
            };

            animate();
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isActive, color, height, width, barCount, visualizationStyle, usingRealAudio, onVolumeChange]);

    // Calculate volume level from analyzer node
    const calculateVolumeLevel = (analyser: AnalyserNode): number => {
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);

        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

        // Normalize to [0, 1]
        return Math.min(1, Math.max(0, average / 255));
    };

    // Draw inactive state (flat bars)
    const drawInactiveState = (ctx: CanvasRenderingContext2D, width: number, height: number, barCount: number, color: string) => {
        const barWidth = Math.max(1, Math.floor(width / barCount) - 2);
        const centerY = height / 2;

        ctx.fillStyle = color;

        // Draw flat bars
        for (let i = 0; i < barCount; i++) {
            const x = i * (barWidth + 2) + 1;
            const barHeight = 4;
            ctx.fillRect(x, centerY - barHeight / 2, barWidth, barHeight);
        }
    };

    // Draw animated bars (fallback when no real audio)
    const drawAnimatedBars = (ctx: CanvasRenderingContext2D, width: number, height: number, barCount: number, color: string) => {
        const barWidth = Math.max(1, Math.floor(width / barCount) - 2);
        const spacing = 2;

        ctx.fillStyle = color;

        // Draw animated bars with varying heights
        for (let i = 0; i < barCount; i++) {
            const x = i * (barWidth + spacing);

            // Create a sin wave effect with varied phases
            const phase = i * 0.2;
            const time = Date.now() / 500;
            const amplitude = 0.4 + 0.6 * Math.sin(time / 3); // Varies between 0.4 and 1.0

            // Generate height using sin wave
            const sinValue = Math.sin(time + phase);
            const normalizedHeight = 0.1 + (0.5 + sinValue * 0.5) * amplitude; // Between 0.1 and 1.0
            const barHeight = normalizedHeight * height;

            ctx.fillRect(x, height - barHeight, barWidth, barHeight);
        }
    };

    // Draw waveform visualization
    const drawWaveform = (ctx: CanvasRenderingContext2D, width: number, height: number, color: string, analyser: AnalyserNode) => {
        const bufferLength = analyser.fftSize;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteTimeDomainData(dataArray);

        // Line style
        ctx.lineWidth = 2;
        ctx.strokeStyle = color;

        // Fill style for area under curve
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, `${color}20`); // Very transparent at top
        gradient.addColorStop(0.5, `${color}40`); // Transparent in middle
        gradient.addColorStop(1, `${color}20`); // Very transparent at bottom
        ctx.fillStyle = gradient;

        // Start path
        ctx.beginPath();

        const sliceWidth = width / bufferLength;
        let x = 0;

        // Start at bottom left
        ctx.moveTo(0, height);

        // Draw bottom line to first point
        const firstValue = dataArray[0] / 128.0;
        const firstY = (firstValue * height) / 2;
        ctx.lineTo(0, height - firstY);

        // Draw the waveform
        for (let i = 0; i < bufferLength; i++) {
            const v = dataArray[i] / 128.0;
            const y = (v * height) / 2;

            ctx.lineTo(x, height - y);

            x += sliceWidth;
        }

        // Draw to bottom right and close path
        ctx.lineTo(width, height);
        ctx.closePath();

        // Fill under the curve
        ctx.fill();

        // Draw the line itself
        ctx.beginPath();
        x = 0;

        for (let i = 0; i < bufferLength; i++) {
            const v = dataArray[i] / 128.0;
            const y = (v * height) / 2;

            if (i === 0) {
                ctx.moveTo(x, height - y);
            } else {
                ctx.lineTo(x, height - y);
            }

            x += sliceWidth;
        }

        ctx.stroke();
    };

    // Draw frequency bars visualization
    const drawBars = (ctx: CanvasRenderingContext2D, width: number, height: number, color: string, analyser: AnalyserNode, barCount: number) => {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        // Create gradient for better visual effect
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, color); // Full color at bottom
        gradient.addColorStop(0.8, color); // Full color near top
        gradient.addColorStop(1, `${color}70`); // Transparent at very top

        ctx.fillStyle = gradient;

        // Calculate bar dimensions
        const barWidth = Math.max(1, Math.floor(width / barCount) - 2);
        const spacing = 2;

        // Determine how to sample the frequency data
        const samplingRate = Math.max(1, Math.floor(bufferLength / barCount));

        // Draw bars
        for (let i = 0; i < barCount; i++) {
            // Sample from lower frequencies more densely
            // This makes the visualization more responsive to speech
            const frequencyIndex = Math.min(bufferLength - 1, Math.floor(Math.pow(i / barCount, 1.8) * barCount * samplingRate));

            const value = dataArray[frequencyIndex];
            const barHeight = (value / 255) * height;

            const x = i * (barWidth + spacing);
            ctx.fillRect(x, height - barHeight, barWidth, barHeight);
        }
    };

    // Draw rounded bars visualization
    const drawRoundedBars = (ctx: CanvasRenderingContext2D, width: number, height: number, color: string, analyser: AnalyserNode, barCount: number) => {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        // Create gradient
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, color);
        gradient.addColorStop(0.8, color);
        gradient.addColorStop(1, `${color}70`);

        ctx.fillStyle = gradient;

        // Calculate dimensions
        const barWidth = Math.max(2, Math.floor(width / barCount) - 2);
        const spacing = 2;
        const cornerRadius = Math.min(4, Math.floor(barWidth / 2));

        // Sample frequency data
        const samplingRate = Math.max(1, Math.floor(bufferLength / barCount));

        // Draw rounded bars
        for (let i = 0; i < barCount; i++) {
            // Use exponential scaling to emphasize lower frequencies
            const frequencyIndex = Math.min(bufferLength - 1, Math.floor(Math.pow(i / barCount, 1.4) * barCount * samplingRate));

            const value = dataArray[frequencyIndex];
            let barHeight = (value / 255) * height;

            // Ensure minimum height for rounded corners
            barHeight = Math.max(cornerRadius * 2, barHeight);

            const x = i * (barWidth + spacing);

            // Draw rounded rectangle
            roundRect(ctx, x, height - barHeight, barWidth, barHeight, { tl: cornerRadius, tr: cornerRadius, bl: 0, br: 0 });
        }
    };

    // Draw circular visualization
    const drawCircular = (ctx: CanvasRenderingContext2D, width: number, height: number, color: string, analyser: AnalyserNode) => {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        // Calculate dimensions
        const centerX = width / 2;
        const centerY = height / 2;
        const maxRadius = Math.min(centerX, centerY) * 0.9;
        const baseRadius = maxRadius * 0.3; // Minimum radius

        // Clear with slight background
        ctx.fillStyle = `${color}10`;
        ctx.fillRect(0, 0, width, height);

        // Create gradient for circle
        const gradient = ctx.createRadialGradient(centerX, centerY, baseRadius, centerX, centerY, maxRadius);
        gradient.addColorStop(0, `${color}80`);
        gradient.addColorStop(1, color);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;

        // Draw circle
        ctx.beginPath();

        // Use only a subset of frequency data
        const points = 32; // Number of points to draw
        const angleStep = (2 * Math.PI) / points;

        for (let i = 0; i <= points; i++) {
            // Map to frequency data
            const dataIndex = Math.floor((i / points) * bufferLength * 0.5); // Use first half of frequencies

            const angle = i * angleStep;
            const value = dataArray[dataIndex] / 255.0;

            // Calculate radius based on audio data
            const r = baseRadius + value * (maxRadius - baseRadius);

            // Calculate point
            const x = centerX + Math.cos(angle) * r;
            const y = centerY + Math.sin(angle) * r;

            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }

        ctx.closePath();
        ctx.stroke();

        // Add some "glow" by drawing transparent circle in center
        ctx.fillStyle = `${color}30`;
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, 2 * Math.PI);
        ctx.fill();
    };

    // Helper function to draw rounded rectangles
    const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: { tl: number; tr: number; bl: number; br: number }) => {
        ctx.beginPath();
        ctx.moveTo(x + radius.tl, y);
        ctx.lineTo(x + width - radius.tr, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
        ctx.lineTo(x + width, y + height - radius.br);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
        ctx.lineTo(x + radius.bl, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
        ctx.lineTo(x, y + radius.tl);
        ctx.quadraticCurveTo(x, y, x + radius.tl, y);
        ctx.closePath();
        ctx.fill();
    };

    return (
        <div className="flex items-center justify-center">
            <canvas ref={canvasRef} width={width} height={height} className={`transition-opacity duration-300 ${isActive ? "opacity-100" : "opacity-50"}`} />
        </div>
    );
};

export default AudioWave;
